// The SPA's API client (module-10 Section 1): same-origin fetch, JSON, `x-csrf-token` on writes,
// `{ data }` envelopes unwrapped, problem+json turned into a typed ApiError.
//
// What happens on errors is decided once, in lib/error-policy.ts (the QueryCache / MutationCache handlers):
//   401              -> full-page sign-in, returning to the current page (REQ-18.2)
//   403              -> the page shows <ErrorState> "You don't have access to this"
//   410 gone         -> <ErrorState> "This was removed" with the server's removal message; writes toast it
//   409 revision     -> toast "This ticket has changed" and the ticket is refetched (C13)
import type { FieldError } from '@feedback/shared/contracts/common';

/** Every API call gives up after this long (CLAUDE.md: timeout all external calls). */
const REQUEST_TIMEOUT_MS = 30_000;
/** Uploads carry up to 10 MB files. */
const UPLOAD_TIMEOUT_MS = 120_000;

const SAFE_METHODS = new Set(['GET', 'HEAD']);
const FRIENDLY_FALLBACK = 'Something went wrong. Try again in a moment.';
const TIMEOUT_DETAIL = 'The Feedback tool took too long to answer. Try again.';
const NETWORK_DETAIL = 'We couldn’t reach the Feedback tool. Check your connection and try again.';

export class ApiError extends Error {
  readonly status: number;
  /** Stable problem code: revision_conflict, forbidden, not_found, validation_failed... */
  readonly code: string;
  /** Friendly text from the server, safe to show. */
  readonly detail: string;
  readonly errors: FieldError[];
  /** Set on 409 revision_conflict. */
  readonly currentRevision: number | undefined;
  /** The whole problem body, for code-specific fields. */
  readonly problem: Record<string, unknown>;

  constructor(status: number, problem: Record<string, unknown>) {
    const detail = typeof problem.detail === 'string' && problem.detail ? problem.detail : FRIENDLY_FALLBACK;
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.code = typeof problem.code === 'string' ? problem.code : status === 0 ? 'network' : 'internal';
    this.detail = detail;
    this.errors = Array.isArray(problem.errors) ? (problem.errors as FieldError[]) : [];
    this.currentRevision = typeof problem.currentRevision === 'number' ? problem.currentRevision : undefined;
    this.problem = problem;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isRevisionConflict(error: unknown): error is ApiError {
  return isApiError(error) && error.status === 409 && error.code === 'revision_conflict';
}

// ---- CSRF token: set from GET /api/session (lib/session.ts), sent on every write ----------------

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function hasCsrfToken(): boolean {
  return csrfToken !== null;
}

// ---- Requests ---------------------------------------------------------------------------------------

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Sent as JSON. */
  body?: unknown;
  /** Sent as multipart (uploads); replaces body. */
  form?: FormData;
  /** Query string; arrays become repeated keys, undefined / false / '' are left out. */
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export type QueryValue = string | number | boolean | undefined | null | readonly (string | number)[];

/** Builds `?a=1&b=x&b=y`. Repeated keys are what the queue and dashboard filters take. */
export function toQueryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '' || value === false) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

function timeoutSignal(ms: number, signal: AbortSignal | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(ms);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

/** Calls the tool's API and returns `data` from the `{ data }` envelope (undefined for 204). */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? (options.body !== undefined || options.form ? 'POST' : 'GET');
  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  if (!SAFE_METHODS.has(method) && csrfToken) {
    headers['x-csrf-token'] = csrfToken;
  }
  let response: Response;
  try {
    response = await fetch(`${path}${toQueryString(options.query)}`, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: timeoutSignal(options.form ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS, options.signal),
    });
  } catch (error) {
    if (options.signal?.aborted) {
      throw error;
    }
    const timedOut = error instanceof DOMException && error.name === 'TimeoutError';
    throw new ApiError(0, {
      code: timedOut ? 'timeout' : 'network',
      detail: timedOut ? TIMEOUT_DETAIL : NETWORK_DETAIL,
    });
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const isJson = (response.headers.get('content-type') ?? '').includes('json');
  const payload: unknown = isJson ? await response.json().catch(() => null) : null;
  if (!response.ok) {
    throw new ApiError(response.status, (payload as Record<string, unknown> | null) ?? {});
  }
  return (payload as { data: T } | null)?.data as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(path, { method: 'POST', body: body ?? {}, ...(headers ? { headers } : {}) }),
  put: <T>(path: string, body: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T = void>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  /** Multipart upload of one file in the field the upload routes read (`file`). */
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiRequest<T>(path, { method: 'POST', form });
  },
};

/** Path segment encoding for references and public IDs. */
export const seg = encodeURIComponent;

// ---- Sign-in redirects ---------------------------------------------------------------------------------

/** The current page as a relative path, for returnTo. */
export function currentPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function signInPath(returnTo: string = currentPath()): string {
  return `/sign-in?returnTo=${encodeURIComponent(returnTo)}`;
}

let redirecting = false;

/** Full-page navigation to S1, returning to the current page after sign-in (REQ-18.2). */
export function redirectToSignIn(): void {
  if (redirecting || window.location.pathname === '/sign-in') {
    return;
  }
  redirecting = true;
  window.location.assign(signInPath());
}
